import { useState } from "react"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

// IMP-458: the business's brand color, detected by the onboarding scrape and
// editable wherever the profile is. Stored as "#rrggbb"; null/"" means "not
// detected" and the pickers fall back to this default.
export const DEFAULT_BRAND_COLOR = "#2563eb"

const HEX = /^#[0-9a-f]{6}$/i

export function brandColorOrDefault(value: string | null | undefined) {
  return value && HEX.test(value) ? value.toLowerCase() : DEFAULT_BRAND_COLOR
}

export function BrandColorSwatch({
  color,
  className,
}: {
  color: string | null | undefined
  className?: string
}) {
  return (
    <span
      className={cn("border-border/60 size-4 rounded-full border", className)}
      style={{ backgroundColor: brandColorOrDefault(color) }}
    />
  )
}

// Native picker plus a hex field. The hex field only ever emits "" or a
// complete "#rrggbb", so it can't produce a value the model rejects.
export function BrandColorInput({
  id,
  value,
  onChange,
}: {
  id?: string
  value: string | null
  onChange: (value: string) => void
}) {
  // The field owns its text so a half-typed hex ("#0f7") can sit here without
  // being pushed upward. Callers remount it to re-seed from a new value.
  const [text, setText] = useState(value ?? "")

  function handleText(raw: string) {
    const digits = raw.replace(/[^0-9a-f]/gi, "").slice(0, 6)
    const next = digits === "" ? "" : `#${digits.toLowerCase()}`
    setText(next)
    if (next === "" || HEX.test(next)) onChange(next)
  }

  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="color"
        aria-label="Brand color"
        value={brandColorOrDefault(text)}
        onChange={(e) => {
          setText(e.target.value)
          onChange(e.target.value)
        }}
        className="border-input size-9 shrink-0 cursor-pointer appearance-none rounded-md border bg-transparent p-1 [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none [&::-webkit-color-swatch-wrapper]:p-0"
      />
      <Input
        value={text}
        onChange={(e) => handleText(e.target.value)}
        // An incomplete hex isn't a color — snap back to what was committed.
        onBlur={() => {
          if (text !== "" && !HEX.test(text)) setText(value ?? "")
        }}
        placeholder={DEFAULT_BRAND_COLOR}
        className="w-32"
      />
    </div>
  )
}
